import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';

const IVTherapy = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const ivDrips = [
    {
      name: 'Custom IV Drip',
      price: '$100',
      ingredients: ['Vitamins', 'Minerals', 'Electrolytes']
    },
    {
      name: 'Recovery & Performance',
      price: '$100',
      ingredients: ['Fluids', 'Electrolytes', 'Vitamin C', 'B-Complex']
    },
    {
      name: 'Get Up & Go',
      price: '$100',
      ingredients: ['Fluids', 'Electrolytes', 'B-Complex', 'Vit. C', 'Amino Acids']
    },
    {
      name: 'Hangover',
      price: '$100',
      ingredients: ['Fluids', 'Electrolytes', 'Vitamin C', 'B-Complex', 'Anti-nausea/Anti-inflammatory']
    },
    {
      name: 'Stomach Flu',
      price: '$100',
      ingredients: ['Fluids', 'Electrolytes', 'Pepcid', 'Pepcid (50mg)', 'Zofran']
    },
    {
      name: 'Vitality Hydration',
      price: '$100',
      ingredients: ['Fluids', 'Electrolytes', 'Biotin']
    },
    {
      name: 'Skinny Drip',
      price: '$100',
      ingredients: ['Fluids', 'L-carnitine', 'B-Complex', 'Chromium', 'Taurine', 'Amino Acids']
    },
    {
      name: 'Myers Drip',
      price: '$100',
      ingredients: ['Fluids', 'Magnesium', 'Vitamin C', 'B-Complex and more']
    },
    {
      name: 'Glow',
      price: '$100',
      ingredients: ['Fluids', 'Glutathione', 'Vitamin C', 'Biotin']
    },
    {
      name: 'Immune Booster',
      price: '$100',
      ingredients: ['Fluids', 'Electrolytes', 'Vitamin C', 'Zinc', 'B-Complex']
    }
  ];

  const benefits = [
    {
      title: 'Enhanced Energy Levels',
      description: 'Quickly replenishes essential vitamins and minerals to combat fatigue and boost vitality.'
    },
    {
      title: 'Faster Immune and Symptom Relief',
      description: 'Delivers nutrients directly to the bloodstream for rapid immune support and relief from illness symptoms.'
    },
    {
      title: 'Improved Hydration',
      description: 'Provides immediate and effective hydration, superior to oral intake.'
    },
    {
      title: 'Enhanced Athletic Performance',
      description: 'Supports muscle recovery, reduces inflammation, and improves endurance and performance.'
    },
    {
      title: 'Better Nutrient Absorption',
      description: 'Bypasses the digestive system for 100% absorption of vitamins and minerals.'
    },
    {
      title: 'Supports Mental Clarity',
      description: 'Improves cognitive function, focus, and mental sharpness through optimal nutrient delivery.'
    },
    {
      title: 'Skin Health and Glow',
      description: 'Promotes radiant, healthy skin with antioxidants and hydration from within.'
    },
    {
      title: 'Hangover Relief',
      description: 'Quickly alleviates hangover symptoms by rehydrating and replenishing lost nutrients.'
    },
    {
      title: 'Detoxification of the Body',
      description: 'Helps flush out toxins and supports liver function for overall wellness.'
    },
    {
      title: 'Customizable Treatments',
      description: 'Tailored IV formulas to address your specific health needs and wellness goals.'
    },
    {
      title: 'Reduction in Symptoms of Migraines and Chronic Pain',
      description: 'Provides relief from headaches, migraines, and chronic pain conditions.'
    },
    {
      title: 'Improved Mood',
      description: 'Balances neurotransmitters and reduces stress for enhanced emotional well-being.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>IV Therapy & Vitamin Infusions in Forest Hills, NY | Century Aesthetics</title>
        <meta name="description" content="IV vitamin therapy and nutrient infusions for energy, immunity, and wellness. NAD+, B12, hydration therapy and more in Forest Hills, NY." />
        <meta name="keywords" content="IV therapy, vitamin infusion, NAD+ therapy, hydration therapy, IV vitamins, wellness infusion, IV drip" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/iv-therapy" />
        
        <meta property="og:title" content="IV Therapy & Vitamin Infusions | Century Longevity & Aesthetics" />
        <meta property="og:description" content="IV vitamin therapy and nutrient infusions for energy, immunity, and wellness. NAD+, B12, hydration therapy and more in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/iv-therapy" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">
     

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/ivtherapy.webp" 
                  alt="IV Therapy Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6 uppercase">
                IV Vitamin Health
              </h2>
              <p className="text-2xl font-heading text-gold mb-4 italic">
                Rapidly and efficiently replenish the body's nutrient levels with our IV infusions
              </p>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong className="text-olive-darkest">Wellness is personal—so is your drip.</strong>
                </p>
                <p>
                  IV Therapy has a lot of advantages such as rapid nutrient absorption, immediate hydration, and tailored nutrient formulations - that's what makes it so unique and effective!
                </p>
                
             
                <p className="italic text-gray-600 border-l-4 border-gold pl-4">
                  "The response to IV therapy can vary from person to person, and what works for one individual may not work as effectively for another."
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-12 bg-cream-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6 uppercase">About Us</h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              <p className="text-lg text-gray-700 leading-relaxed italic mb-6">
                Our professional medical team has an experience of over 10 years of administrating IV infusions and can guarantee the highest levels of service.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6 uppercase">What to Expect</h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              <p className="text-lg text-gray-700 leading-relaxed">
                During your appointment, a healthcare provider will review your medical history, discuss your specific health goals, and customize the IV formulation to your needs. At the end of the session, you may start experiencing an improvement in energy or hydration!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personalized Wellness Section */}
      <section className="py-12 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4 uppercase">Personalized Wellness Starts Here</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-olive-50 rounded-lg p-8 shadow-lg">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-heading font-semibold text-olive-darkest mb-4 flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold text-white font-bold">✓</span>
                    Step 1: Choose Your Base
                  </h3>
                  <ul className="ml-12 space-y-2 text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Hydration Base</strong> – Electrolytes & Normal Saline</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Wellness Base</strong> – Hydration + B-Complex Vitamins</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-heading font-semibold text-olive-darkest mb-4 flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold text-white font-bold">✓</span>
                    Step 2: Select Your Boosters (pick up to 3)
                  </h3>
                  <div className="ml-12 grid md:grid-cols-2 gap-3 text-gray-700">
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Vitamin C</strong> – Immunity & Antioxidant Support</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>B12</strong> – Energy, Metabolism & Focus</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Glutathione</strong> – Skin Glow & Detox</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Magnesium</strong> – Relaxation & Stress Relief</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Zinc</strong> – Immune Defense</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Amino Acids</strong> – Performance & Recovery</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Biotin</strong> – Hair, Skin, & Nails</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Anti-Nausea</strong> – Quick Stomach Relief</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Anti-inflammatory</strong> – Pain & Inflammation Support</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>NAD+</strong> – Energy, Cognitive Function</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg text-gray-600 italic">
                  Wellness is personal—so is your drip.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1000px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="text-gold text-6xl mb-6">"</div>
            <p className="text-xl md:text-2xl text-white leading-relaxed italic mb-6">
              I recently tried IV therapy for an energy boost, and the results were impressive. After the infusion, I felt noticeably more energetic and alert
            </p>
            <p className="text-gold font-semibold text-lg">- SAMANTHA</p>
          </motion.div>
        </div>
      </section>

      {/* Call to Book Section */}
      <section className="py-12 bg-cream-50">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6 uppercase">Call to Book</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              If you are ready to take your wellness routine to the next level, make the booking with us and give yourself the best treatment your body deserves!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="bg-olive-darkest text-white px-12 py-4 rounded-lg font-semibold text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Appointment
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* IV Drips Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-2 uppercase">Your Drip Your Way</h2>
            <p className="text-xl text-gold italic mb-4">Wellness is personal—so is your drip.</p>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ivDrips.map((drip, index) => (
              <IVDripCard
                key={drip.name}
                drip={drip}
                index={index}
                scrollToContact={scrollToContact}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of IV Therapy</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Experience the immediate revitalizing benefits of IV Therapy. Boost your energy, support your immune system, and feel your best!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about IV therapy options, what to expect during treatment, and how we can customize a drip for your wellness goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('iv-therapy');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

// IV Drip Card Component with Expandable Details
const IVDripCard = ({ drip, index, scrollToContact }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
      className="bg-white rounded-lg p-6 shadow-xl"
    >
      {/* IV Bag Image Placeholder */}
      <div className="mb-4 flex justify-center">
        <div className="w-32 h-40 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border-2 border-olive-darkest/20">
          <div className="text-center">
            <div className="w-16 h-20 mx-auto bg-olive-darkest rounded mb-2 flex items-center justify-center">
              <span className="text-white text-xs font-bold">{drip.name.split(' ')[0]}</span>
            </div>
            <div className="w-2 h-8 bg-olive-darkest/30 mx-auto"></div>
          </div>
        </div>
      </div>

      <h3 className="text-xl font-heading font-semibold text-olive-darkest text-center mb-2 uppercase tracking-wide">
        {drip.name}
      </h3>
      <p className="text-lg font-body text-gold text-center mb-4 font-medium">
        {drip.price}
      </p>

      {/* Expandable Ingredients */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-olive-darkest hover:text-gold transition-colors duration-300 mb-4"
      >
        SHOW MORE DETAILS
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-4 pt-4 border-t border-gray-200"
        >
          <h4 className="text-sm font-semibold text-olive-darkest mb-2 uppercase">Ingredients:</h4>
          <ul className="space-y-1">
            {drip.ingredients.map((ingredient, idx) => (
              <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                <span className="text-gold mt-1">•</span>
                <span>{ingredient}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      <motion.button
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.98 }}
        onClick={scrollToContact}
        className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
      >
        Book Now
      </motion.button>
    </motion.div>
  );
};

export default IVTherapy;